/**
 * Live-Provisionsrechner. 20 % auf das Monatsabo (39 €) = 7,80 € pro Empfehlung,
 * wiederkehrend. Hochrechnung auf 12 Monate (ehrliche, einfache Mathematik).
 */
import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";

export const COMMISSION_RATE = 0.2;
export const MONTHLY_PRICE_EUR = 39;
export const COMMISSION_PER_REFERRAL_EUR = MONTHLY_PRICE_EUR * COMMISSION_RATE; // 7,80 €

export function calcMonthlyEarnings(referrals: number): number {
  return referrals * COMMISSION_PER_REFERRAL_EUR;
}

export function calcYearlyEarnings(referrals: number): number {
  return referrals * COMMISSION_PER_REFERRAL_EUR * 12;
}

const formatEur = (value: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(value);

const PartnerEarningsCalculator = () => {
  const [referrals, setReferrals] = useState(5);

  const monthly = useMemo(() => calcMonthlyEarnings(referrals), [referrals]);
  const yearly = useMemo(() => calcYearlyEarnings(referrals), [referrals]);

  return (
    <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16">
      <div className="container mx-auto max-w-3xl px-6">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-3xl font-bold">Was kannst du verdienen?</h2>
          <p className="text-muted-foreground">
            20 % Provision auf die MusiKonzept-Mitgliedschaft (39 € im Monat) – also <strong>7,80 € pro Empfehlung</strong>,
            jeden Monat, solange dein Empfohlener dabei ist.
          </p>
        </div>

        <div className="rounded-2xl border bg-card p-6 shadow-sm sm:p-8">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-sm font-medium text-muted-foreground">Empfehlungen pro Monat</span>
            <span className="text-2xl font-bold text-primary">{referrals}</span>
          </div>
          <Slider
            value={[referrals]}
            onValueChange={(v) => setReferrals(v[0] ?? 0)}
            min={1}
            max={50}
            step={1}
            aria-label="Empfehlungen pro Monat"
          />

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl bg-primary/10 p-5 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Monatlich wiederkehrend</div>
              <div className="mt-1 text-3xl font-bold text-foreground">{formatEur(monthly)}</div>
              <div className="mt-1 text-xs text-muted-foreground">jeden Monat aufs Neue</div>
            </div>
            <div className="rounded-xl bg-accent/15 p-5 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Nach 12 Monaten</div>
              <div className="mt-1 text-3xl font-bold text-foreground">{formatEur(yearly)}</div>
              <div className="mt-1 text-xs text-muted-foreground">wenn deine Empfohlenen dabei bleiben</div>
            </div>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            Beispielrechnung. Was tatsächlich rauskommt, hängt davon ab, wie lange deine Empfohlenen Mitglied bleiben.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PartnerEarningsCalculator;
